import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Edit, Clock, AlertCircle, Gamepad2 } from 'lucide-react';
import { FILL_GRADIENT } from '../../constants/gradients';
import CategoryTag from '../common/CategoryTag';
import UserAvatar from '../common/UserAvatar';
import { User } from '../../services/userService';
import { analyticsService } from '../../services/analyticsService';

interface EditorEdit {
  id: string;
  summary: string;
  role: string;
  model?: string;
  generated: boolean;
  timestamp: string;
  isActive: boolean;
  onCurrentPath: boolean;
  isFutureEdit: boolean;
  version_number?: number;
  created_at?: string;
  updated_at?: string;
  code_hash?: string;
}

interface Review {
  id: string;
  editor_edit_id: string;
  status: string;
}

interface PublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameContent: any; // Current game content
  screenshots?: { all: string[]; selected: number[] };
  onSubmitReview: (editorEditId: string) => Promise<void>;
  editHistory: EditorEdit[];
  reviews: Review[];
  onEditDetails: () => void; // Callback to open description modal from parent
  currentUser?: User | null; // Current logged-in user
}

export default function PublishModal({
  isOpen,
  onClose,
  gameContent,
  screenshots,
  onSubmitReview,
  editHistory,
  reviews,
  onEditDetails,
  currentUser
}: PublishModalProps) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Track modal opened
  useEffect(() => {
    if (isOpen) {
      analyticsService.track({
        name: 'publish_modal_opened',
        properties: {
          has_existing_version: reviews.length > 0,
          version_number: reviews.length + 1,
          has_game_content: !!gameContent,
          screenshot_count: screenshots?.selected.length || 0
        }
      });
    }
  }, [isOpen, reviews.length, gameContent, screenshots?.selected.length]);

  if (!isOpen) return null;

  const latestEdit = editHistory.length > 0 ? editHistory[0] : null;
  const selectedScreenshots = screenshots?.all.filter((_, i) => screenshots.selected.includes(i)) || [];
  
  // Calculate version number: next version will be total reviews + 1
  const versionNumber = reviews.length + 1;

  // Extract user name
  const userName = currentUser?.display_name || currentUser?.username || 'You';

  const handlePublish = async () => {
    setLoading(true);
    setErrors([]);
    
    try {
      // Validate project details
      const validationErrors: string[] = [];
      
      if (!gameContent?.name?.trim()) {
        validationErrors.push('Title is required');
      }
      
      if (!gameContent?.tags || gameContent.tags.length === 0) {
        validationErrors.push('At least one category is required');
      }
      
      if (!gameContent?.description?.trim()) {
        validationErrors.push('Description is required');
      }
      
      if (!gameContent?.play_instructions?.trim()) {
        validationErrors.push('Play instructions are required');
      }
      
      
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
      }
      
      // Submit for review
      if (latestEdit) {
        console.log('📤 Submitting latest edit for review:', latestEdit.id);
        await onSubmitReview(latestEdit.id);
        
        // Track successful publish
        analyticsService.track({
          name: 'project_published',
          properties: {
            version_number: versionNumber,
            has_changelog: !!latestEdit.summary,
            screenshot_count: selectedScreenshots.length,
            has_game_content: !!gameContent,
            edit_id: latestEdit.id
          }
        });
      }
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to submit for review:', error);
      
      // Track publish failure
      analyticsService.track({
        name: 'publish_failed',
        properties: {
          error_type: 'submission_error',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        }
      });
      
      setErrors(['Failed to submit for review. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Gradient Line at Top */}
              <div className="h-1 w-full" style={{ background: FILL_GRADIENT }} />
              
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Review & Publish</h2>
                <button
                  onClick={onClose}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

        {/* Content - Summary View */}
        <div className="flex-1 overflow-y-auto">
          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="mx-6 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-2">Please complete the following before publishing:</h3>
                  <ul className="space-y-1 text-sm text-red-800 dark:text-red-200">
                    {errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Cover Image */}
          <div className="relative h-48 bg-gray-200 dark:bg-gray-800">
            {gameContent?.cover_image_url ? (
              <img 
                src={gameContent.cover_image_url} 
                alt="Game cover" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm font-medium">
                No cover image
              </div>
            )}
          </div>

          {/* Icon - Overlapping Cover */}
          <div className="flex justify-center -mt-12 px-6 relative z-10">
            {gameContent?.icon_url ? (
              <img 
                src={gameContent.icon_url} 
                alt="Game icon" 
                className="w-24 h-24 rounded-xl object-cover bg-white dark:bg-gray-900 border-4 border-white dark:border-gray-900 shadow-lg"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-purple-400 to-pink-400 border-4 border-white dark:border-gray-900 shadow-lg flex items-center justify-center text-white">
                <Gamepad2 className="w-10 h-10" />
              </div>
            )}
          </div>

          {/* Game Info */}
          <div className="px-8 pt-4 pb-6 space-y-4">
            {/* Game Name */}
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
              {gameContent?.name || 'Untitled Game'}
            </h1>

            {/* Categories */}
            <div className="flex flex-wrap justify-center gap-2">
              {gameContent?.tags && gameContent.tags.length > 0 ? (
                gameContent.tags.map((tag: string) => (
                  <CategoryTag key={tag} categoryId={tag} size="md" showIcon={true} />
                ))
              ) : (
                <span className="text-gray-400 dark:text-gray-500 text-sm">No categories</span>
              )}
            </div>

            {/* Description */}
            <p className="text-sm pb-4 text-gray-700 dark:text-gray-300 text-center whitespace-pre-wrap leading-relaxed">
              {gameContent?.description || 'No description'}
            </p>

            {/* Screenshots */}
            {selectedScreenshots.length > 0 ? (
              <div className="relative -mx-8 px-8">
                <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory justify-center scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                  {selectedScreenshots.map((url: string, index: number) => (
                    <div
                      key={index}
                      className="flex-shrink-0 snap-start"
                      style={{ width: '180px' }}
                    >
                      <img
                        src={url}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm object-cover"
                        style={{ aspectRatio: '3/4', height: '240px' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-gray-400 dark:text-gray-500 text-sm">No screenshots selected</p>
              </div>
            )}

            {/* Play Instructions */}
            {gameContent?.play_instructions && (
              <div className="mt-6 py-4 mx-20 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">How to Play</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">
                  {gameContent.play_instructions}
                </p>
              </div>
            )}

            {/* Version Details */}
            {latestEdit && (
              <div className="mt-8 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Version Details</h3>
                
                {/* Version Number - Prominent */}
                <div className="mb-3">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    Version {versionNumber}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{formatDate(latestEdit.updated_at || latestEdit.created_at || latestEdit.timestamp)}</span>
                  </div>
                </div>

                {/* Author */}
                <div className="flex items-center gap-2 mb-3">
                  <UserAvatar currentUser={currentUser} size="sm" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{userName}</span>
                </div>

                {latestEdit.code_hash && (
                  <div className="mb-3">
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{latestEdit.code_hash.substring(0, 16)}</span>
                  </div>
                )}
                
                {latestEdit.summary && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm italic text-gray-600 dark:text-gray-400">"{latestEdit.summary}"</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sticky Footer with Buttons */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-6 py-4">
          <div className="flex justify-end gap-3">
            <button
              onClick={onEditDetails}
              disabled={loading}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 border ${
                loading
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Edit className="w-4 h-4" />
              Edit Details
            </button>
            <button
              onClick={handlePublish}
              disabled={loading}
              className={`px-6 py-2 text-sm font-semibold rounded-lg transition-all ${
                loading
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500'
                  : 'text-white hover:shadow-lg hover:scale-105 active:scale-95'
              }`}
              style={loading ? {} : { background: FILL_GRADIENT }}
            >
              {loading ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>
        </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

